/**
 * ChaseOffersDialog — bulk-outreach modal for the "Chase Unaccepted Offers"
 * action on the Recruitment Funnel's Offers Made card.
 *
 * Lists every student whose offer hasn't been accepted, with per-row
 * WhatsApp (wa.me deep-link) and Email nudge actions plus a bulk "Send
 * Email to All Contactable" button. Nudges are logged in the
 * application's journey_history on the backend.
 */
import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, MessageCircle, Mail, ExternalLink, Send, Users } from 'lucide-react';

const DEFAULT_SUBJECT = 'Quick check-in on your offer from {university} — H E Direct';
const DEFAULT_MESSAGE =
`Hi {name},

Hope you're doing well! We wanted to check in on your {offer_type} offer from {university} for {course}.

Is there anything holding you back from accepting? We can walk you through any outstanding conditions, deposit questions, or scholarships available for the September 2026 intake.

Just reply to this email or WhatsApp us — we're happy to hop on a quick call.

Best,
H E Direct Team`;

export default function ChaseOffersDialog({ open, onOpenChange, intake = 'September 2026', scope = 'all' }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);
  const [nudging, setNudging] = useState(new Set()); // app_ids currently being nudged

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/chase/unaccepted-offers', { params: { intake, scope, vertical: 'all' } })
      .then(r => {
        setRows(r.data.students || []);
        // Preselect all email-contactable rows
        setSelected(new Set((r.data.students || []).filter(s => s.email_contactable).map(s => s.application_id)));
      })
      .catch(err => {
        toast.error('Failed to load pending offers');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [open, intake, scope]);

  const contactableRows = useMemo(() => rows.filter(r => r.email_contactable || r.phone_contactable), [rows]);

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAllEmail = () => {
    const emailable = rows.filter(r => r.email_contactable).map(r => r.application_id);
    if (emailable.every(id => selected.has(id))) {
      const next = new Set(selected);
      emailable.forEach(id => next.delete(id));
      setSelected(next);
    } else {
      setSelected(new Set([...selected, ...emailable]));
    }
  };

  const sendOneNudge = async (row, channel = 'email') => {
    if (channel === 'whatsapp_log') {
      window.open(row.wa_link, '_blank', 'noopener');
    }
    setNudging(prev => new Set(prev).add(row.application_id));
    try {
      const res = await api.post('/chase/send-nudge', {
        application_ids: [row.application_id],
        channel,
        subject,
        message,
      });
      const r = res.data.results?.[0];
      if (r?.ok) toast.success(channel === 'email' ? `Email sent to ${row.full_name}` : `Logged WhatsApp nudge for ${row.full_name}`);
      else toast.error(`${row.full_name}: ${r?.reason || 'failed'}`);
    } catch (e) {
      toast.error(`${row.full_name}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setNudging(prev => { const next = new Set(prev); next.delete(row.application_id); return next; });
    }
  };

  const sendBulk = async () => {
    const ids = [...selected].filter(id => rows.find(r => r.application_id === id)?.email_contactable);
    if (!ids.length) { toast.warning('No contactable emails selected.'); return; }
    setSending(true);
    try {
      const res = await api.post('/chase/send-nudge', {
        application_ids: ids, channel: 'email', subject, message,
      });
      toast.success(`Sent ${res.data.ok} of ${ids.length} nudges (${res.data.failed} failed)`);
    } catch (e) {
      toast.error(`Bulk send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    email: rows.filter(r => r.email_contactable).length,
    phone: rows.filter(r => r.phone_contactable).length,
    stale: rows.filter(r => (r.days_since_offer || 0) >= 7).length,
  }), [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col bg-white dark:bg-slate-900" data-testid="chase-offers-dialog">
        <DialogHeader className="pb-3 border-b border-gray-100 dark:border-slate-800">
          <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
            <MessageCircle className="w-5 h-5 text-teal-600" />
            Chase Unaccepted Offers
            <Badge variant="outline" className="ml-2 text-xs">{intake}</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
            Students who received an offer but haven&apos;t accepted yet. Nudge them via WhatsApp or email to move them into the Accepted / Deposit Paid stage.
          </DialogDescription>
          <div className="flex flex-wrap gap-3 mt-3 text-xs">
            <span className="text-gray-600 dark:text-gray-300"><b className="text-teal-700 dark:text-teal-300">{stats.total}</b> pending</span>
            <span className="text-gray-600 dark:text-gray-300"><b className="text-emerald-700 dark:text-emerald-300">{stats.email}</b> emailable</span>
            <span className="text-gray-600 dark:text-gray-300"><b className="text-blue-700 dark:text-blue-300">{stats.phone}</b> whatsapp</span>
            <span className="text-gray-600 dark:text-gray-300"><b className="text-rose-700 dark:text-rose-300">{stats.stale}</b> stale (7d+)</span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-3 gap-4 pt-4">
          {/* Template editor */}
          <div className="lg:col-span-1 space-y-3 overflow-y-auto pr-2" data-testid="chase-template-editor">
            <div>
              <Label className="text-xs">Email Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="chase-subject" className="text-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs">Email Message</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} data-testid="chase-message" rows={12} className="text-xs mt-1 font-mono" />
              <div className="text-[10px] text-gray-500 mt-1">
                Placeholders: <code>{'{name}'}</code>, <code>{'{university}'}</code>, <code>{'{course}'}</code>, <code>{'{offer_type}'}</code>, <code>{'{reference_code}'}</code>
              </div>
            </div>
            <Button
              onClick={sendBulk}
              disabled={sending || selected.size === 0}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="chase-send-bulk"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email to Selected ({selected.size})
            </Button>
            <div className="text-[10px] text-gray-500 leading-tight">
              Non-contactable rows (missing/imported email) are skipped automatically. Every send is logged in the application&apos;s journey history.
            </div>
          </div>

          {/* Student table */}
          <div className="lg:col-span-2 overflow-hidden flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading pending offers...
              </div>
            ) : rows.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-gray-500 text-sm text-center px-8">
                <div>
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No students with unaccepted offers. Either everyone accepted or no offers have been issued yet.
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={toggleAllEmail}
                    className="text-xs text-teal-600 dark:text-teal-400 hover:underline"
                    data-testid="chase-toggle-all"
                  >
                    Toggle all emailable
                  </button>
                  <span className="text-xs text-gray-500">
                    {selected.size} of {contactableRows.length} contactable selected
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-slate-700 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0">
                      <tr className="text-left text-gray-600 dark:text-gray-300">
                        <th className="p-2 w-8"></th>
                        <th className="p-2">Student</th>
                        <th className="p-2">Offer / University</th>
                        <th className="p-2">Type</th>
                        <th className="p-2 text-center">Days</th>
                        <th className="p-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                      {rows.map(r => {
                        const stale = (r.days_since_offer || 0) >= 7;
                        const contactable = r.email_contactable || r.phone_contactable;
                        const isBusy = nudging.has(r.application_id);
                        return (
                          <tr key={r.application_id} className={contactable ? '' : 'opacity-60'} data-testid={`chase-row-${r.application_id}`}>
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={selected.has(r.application_id)}
                                onChange={() => toggleRow(r.application_id)}
                                disabled={!r.email_contactable}
                                data-testid={`chase-check-${r.application_id}`}
                              />
                            </td>
                            <td className="p-2">
                              <div className="font-medium text-gray-900 dark:text-white">{r.full_name}</div>
                              <div className="text-[10px] text-gray-500 truncate max-w-[180px]" title={r.email}>{r.email || 'no email'}</div>
                              {r.phone ? <div className="text-[10px] text-gray-400">{r.phone}</div> : null}
                            </td>
                            <td className="p-2">
                              <div className="text-gray-800 dark:text-gray-200 truncate max-w-[200px]" title={r.university}>{r.university || '—'}</div>
                              <div className="text-[10px] text-gray-500 truncate max-w-[200px]" title={r.course}>{r.course || '—'}</div>
                              {r.source_type === 'agent' && r.agent_name ? (
                                <div className="text-[10px] text-teal-600 dark:text-teal-400">via {r.agent_name}</div>
                              ) : null}
                            </td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-[10px]">{r.offer_type}</Badge>
                            </td>
                            <td className="p-2 text-center">
                              <span className={stale ? 'text-rose-600 font-semibold' : 'text-gray-600'}>
                                {r.days_since_offer ?? '—'}
                              </span>
                            </td>
                            <td className="p-2 text-right whitespace-nowrap">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendOneNudge(r, 'whatsapp_log')}
                                disabled={!r.phone_contactable || isBusy}
                                className="h-7 px-2 text-[11px] mr-1"
                                data-testid={`chase-wa-${r.application_id}`}
                                title={r.phone_contactable ? 'Open WhatsApp with pre-filled message' : 'No phone on file'}
                              >
                                <MessageCircle className="w-3 h-3 mr-1" /> WA <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendOneNudge(r, 'email')}
                                disabled={!r.email_contactable || isBusy}
                                className="h-7 px-2 text-[11px]"
                                data-testid={`chase-email-${r.application_id}`}
                                title={r.email_contactable ? 'Send email nudge now' : 'Missing or imported email'}
                              >
                                {isBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                                Email
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
